import type { MortgageInterestTerm } from '@/types/interest.types'
export type { MortgageInterestTerm }

export const ApplicationAdditionDocumentType = {
  amortizationProof: 'amortizationProof',
  annualReportBRF: 'annualReportBRF',
  apartmentRegister: 'apartmentRegister',
  auditorCertificate: 'auditorCertificate',
  blancoLoanDocumentation: 'blancoLoanDocumentation',
  brokerAssignment: 'brokerAssignment',
  deedOfGift: 'deedOfGift',
  depositAgreement: 'depositAgreement',
  divorcePapers: 'divorcePapers',
  economicPlan: 'economicPlan',
  energyDeclaration: 'energyDeclaration',
  estateDivision: 'estateDivision',
  finalInspection: 'finalInspection',
  financialPlan: 'financialPlan',
  greenRenovation: 'greenRenovation',
  inventoryOfEstate: 'inventoryOfEstate',
  invoiceFinal: 'invoiceFinal',
  loanPurposeDocumentation: 'loanPurposeDocumentation',
  moneySource: 'moneySource',
  paperMortgageDeeds: 'paperMortgageDeeds',
  pensionProof: 'pensionProof',
  pension: 'pension',
  valuation: 'valuation',
  powerOfAttorney: 'powerOfAttorney',
  prenuptialAgreement: 'prenuptialAgreement',
  promissoryNote: 'promissoryNote',
  propertyDescription: 'propertyDescription',
  purchaseContract: 'purchaseContract',
  retaxation: 'retaxation',
  salesContract: 'salesContract',
  testament: 'testament',
  income: 'income',
  capitalAssets: 'capitalAssets',
} as const

export type ApplicationAdditionDocumentType =
  (typeof ApplicationAdditionDocumentType)[keyof typeof ApplicationAdditionDocumentType]

export const ApplicationAdditionType = {
  documentAmortizationProof: 'documents.amortizationProof',
  documentAnnualReportBRF: 'documents.annualReportBRF',
  documentApartmentRegister: 'documents.apartmentRegister',
  documentAuditorCertificate: 'documents.auditorCertificate',
  documentBlancoLoanDocumentation: 'documents.blancoLoanDocumentation',
  documentBrokerAssignment: 'documents.brokerAssignment',
  documentDeedOfGift: 'documents.deedOfGift',
  documentDepositAgreement: 'documents.depositAgreement',
  documentDivorcePapers: 'documents.divorcePapers',
  documentEconomicPlan: 'documents.economicPlan',
  documentEnergyDeclaration: 'documents.energyDeclaration',
  documentEstateDivision: 'documents.estateDivision',
  documentFinalInspection: 'documents.finalInspection',
  documentFinancialPlan: 'documents.financialPlan',
  documentGreenRenovation: 'documents.greenRenovation',
  documentInventoryOfEstate: 'documents.inventoryOfEstate',
  documentInvoiceFinal: 'documents.invoiceFinal',
  documentLoanPurposeDocumentation: 'documents.loanPurposeDocumentation',
  documentMoneySource: 'documents.moneySource',
  documentPaperMortgageDeeds: 'documents.paperMortgageDeeds',
  documentPensionProof: 'documents.pensionProof',
  documentPension: 'documents.pension',
  documentValuation: 'documents.valuation',
  documentPowerOfAttorney: 'documents.powerOfAttorney',
  documentPrenuptialAgreement: 'documents.prenuptialAgreement',
  documentPromissoryNote: 'documents.promissoryNote',
  documentPropertyDescription: 'documents.propertyDescription',
  documentPurchaseContract: 'documents.purchaseContract',
  documentRetaxation: 'documents.retaxation',
  documentSalesContract: 'documents.salesContract',
  documentTestament: 'documents.testament',
  documentIncome: 'documents.income',

  apartmentNbrBRF: 'properties.apartmentNbrBRF',
  homeInsurance: 'properties.homeInsurance',
  housingAssociation: 'properties.housingAssociation',

  bankAccount: 'applications.bankAccount',
  loans: 'applications.loans',
  payoutDate: 'applications.payoutDate',
  plannedCashDisbursement: 'applications.plannedCashDisbursement',
  plannedRepayMortgage: 'applications.plannedRepayMortgage',
  moneySource: 'applications.moneySource',
  updateParties: 'applications.updateParties',
  topupPurpose: 'applications.topupPurpose',
  propertySales: 'applications.propertySales',
  bridgeLoan: 'applications.bridgeLoan',
  depositLoan: 'applications.depositLoan',
  realtorInformation: 'applications.realtorInformation',
  capital: 'applications.capital',
  kyc: 'applications.kyc',
} as const

export type ApplicationAdditionType = (typeof ApplicationAdditionType)[keyof typeof ApplicationAdditionType]

export interface DocumentApplicationAdditionData {
  documentIds: string[]
}

export type PayoutDateApplicationAdditionData =
  | { payoutDateASAP: true; payoutDate?: never; payoutTime?: never }
  | { payoutDateASAP: false; payoutDate: string; payoutTime?: string }

export interface ApartmentNbrBRFApplicationAdditionData {
  apartmentNbrBRF: string
}

export interface HousingAssociationApplicationAdditionData {
  housingAssociation: string
}

/**
 * En bostadsrätt frågas bara om den är försäkrad och hos vilket bolag — omfattningsfälten
 * avvisas av API:t för den bostadstypen.
 */
export interface HomeInsuranceApplicationAdditionData {
  isInsured: boolean
  insuranceCompany?: string
  insuranceCompanyOtherName?: string
  isFullyInsured?: boolean
  coverageAmount?: number
  excessAmount?: number
}

export interface TopupPurposeApplicationAdditionData {
  topupPurpose: string | null
  topupPurposeOther?: string
}

export interface RealtorInformationApplicationAdditionData {
  realtorEmail: string
  realtorPhone: string
}

export interface PlannedCashDisbursementApplicationAdditionData {
  bank: string
  accountNumber: string
}

export interface PlannedRepayMortgageItem {
  bank: string
  loanNumber: string
  amount: number
}

export type PlannedRepayMortgageApplicationAdditionData = PlannedRepayMortgageItem[]

export type MoneySourceType =
  | 'savings'
  | 'saleHouse'
  | 'saleApartment'
  | 'saleStocks'
  | 'income'
  | 'heritage'
  | 'gift'
  | 'loanFromRelated'
  | 'other'
export interface MoneySourceApplicationAdditionItem {
  amount: number
  sourceFreeText?: string
  source: MoneySourceType
}

export type MoneySourceApplicationAdditionData = { moneySources: MoneySourceApplicationAdditionItem[] }

/** Rabatt på ett lån i kompletteringen: räntepunkter (1–1000) och frivillig anledning. */
export interface LoanDiscountInput {
  bps: number
  reason?: string
}

export interface LoansBreakdownItem {
  amount: number
  interestTerm: MortgageInterestTerm
  amortization: number
  discount?: LoanDiscountInput
}

export interface LoansApplicationAdditionData {
  loans: LoansBreakdownItem[]
}

export const PaymentMethod = {
  autogiro: 'autogiro',
} as const

export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod]

export interface BankAccountApplicationAdditionData {
  userId: string
  bankAccount: string
  paymentMethod: PaymentMethod
}

export interface CapitalIncomeApplicationAdditionData {
  capitalAssets: number
  userId: string
  documentIds: string[]
}

export type PartyStatus = 'owner' | 'ownerNotApplicant' | 'married' | 'domesticPartner' | 'applicantNotOwner'

// Does not include owner, since then it should get added as an applicant.
export type PartyStatusNotOwner = Exclude<PartyStatus, 'owner'>

export interface PartyApplicationAdditionItem {
  name: string
  personalNumber: number
  email: string
  status: PartyStatusNotOwner
}

export interface PartiesApplicationAdditionData {
  parties: PartyApplicationAdditionItem[]
}

export interface PropertySaleItem {
  salePrice: number
  purchasePrice: number
  saleFee: number
  deferredTax: number
  saleLoans: number
  taxNow: boolean
  saleCompleteDate: string
}

export interface PropertySalesApplicationAdditionData {
  propertySales: PropertySaleItem[]
}

/**
 * Brygglån eller handpenningslån som förmedlaren ansöker om. Rabatten anges i räntepunkter (1–1000).
 */
export interface UnsecuredLoanApplicationAdditionData {
  amountAppliedFor: number
  discountBps?: number
  discountReason?: string
}

export type UnsupportedApplicationAdditionData = never

/** `kyc` is answered with `submitKycAnswers` (`@/services/kyc`), not submitted as an application addition. */
export type SubmittableApplicationAdditionType = Exclude<ApplicationAdditionType, typeof ApplicationAdditionType.kyc>

export interface ApplicationAdditionDataByType {
  [ApplicationAdditionType.documentAmortizationProof]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentAnnualReportBRF]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentApartmentRegister]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentAuditorCertificate]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentBlancoLoanDocumentation]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentBrokerAssignment]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentDeedOfGift]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentDepositAgreement]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentDivorcePapers]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentEconomicPlan]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentEnergyDeclaration]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentEstateDivision]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentFinalInspection]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentFinancialPlan]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentGreenRenovation]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentInventoryOfEstate]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentInvoiceFinal]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentLoanPurposeDocumentation]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentMoneySource]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentPaperMortgageDeeds]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentPensionProof]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentPension]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentValuation]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentPowerOfAttorney]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentPrenuptialAgreement]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentPromissoryNote]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentPropertyDescription]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentPurchaseContract]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentRetaxation]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentSalesContract]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentTestament]: DocumentApplicationAdditionData
  [ApplicationAdditionType.documentIncome]: DocumentApplicationAdditionData

  [ApplicationAdditionType.apartmentNbrBRF]: ApartmentNbrBRFApplicationAdditionData
  [ApplicationAdditionType.homeInsurance]: HomeInsuranceApplicationAdditionData
  [ApplicationAdditionType.housingAssociation]: HousingAssociationApplicationAdditionData
  [ApplicationAdditionType.bankAccount]: BankAccountApplicationAdditionData
  [ApplicationAdditionType.loans]: LoansApplicationAdditionData
  [ApplicationAdditionType.payoutDate]: PayoutDateApplicationAdditionData
  [ApplicationAdditionType.plannedCashDisbursement]: PlannedCashDisbursementApplicationAdditionData
  [ApplicationAdditionType.plannedRepayMortgage]: PlannedRepayMortgageApplicationAdditionData
  [ApplicationAdditionType.moneySource]: MoneySourceApplicationAdditionData
  [ApplicationAdditionType.updateParties]: PartiesApplicationAdditionData
  [ApplicationAdditionType.topupPurpose]: TopupPurposeApplicationAdditionData
  [ApplicationAdditionType.propertySales]: PropertySalesApplicationAdditionData
  [ApplicationAdditionType.bridgeLoan]: UnsecuredLoanApplicationAdditionData
  [ApplicationAdditionType.depositLoan]: UnsecuredLoanApplicationAdditionData
  [ApplicationAdditionType.realtorInformation]: RealtorInformationApplicationAdditionData
  [ApplicationAdditionType.capital]: CapitalIncomeApplicationAdditionData
}

export interface SubmitApplicationAdditionPayload<
  TType extends SubmittableApplicationAdditionType = SubmittableApplicationAdditionType,
> {
  type: TType
  requestId?: string
  data: ApplicationAdditionDataByType[TType]
}

export interface SubmitApplicationAdditionResponse {
  applicationId: string
  type: ApplicationAdditionType
  requestId: string | null
}

export interface UploadApplicationDocumentPayload {
  file: File
  type: ApplicationAdditionDocumentType
}

export interface UploadApplicationDocumentResponse {
  documentId: string
}
